/**
 * NgRX Entity Adapter for Syndicates List
 *
 * Manages the syndicates collection with built-in CRUD operations
 * and O(1) lookups for updates.
 *
 * IMPORTANT: Sorting is handled by the backend API, not at application level.
 * The backend returns syndicates in the correct order based on sort_by parameter.
 * This ensures that "load more" pagination maintains consistent ordering across pages.
 */

import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { SyndicatesScope } from './syndicates.model';

/**
 * Entity adapter for Syndicate objects
 *
 * sortComparer set to false because backend controls sort order.
 * When calling API with sort_by parameter, backend returns items pre-sorted.
 * The adapter preserves this order when storing in entity state.
 */
export const syndicateAdapter: EntityAdapter<SyndicatesScope.Syndicate> =
  createEntityAdapter<SyndicatesScope.Syndicate>({
    selectId: (syndicate: SyndicatesScope.Syndicate) => String(syndicate.id), // Normalize to string for consistency
    sortComparer: false // Dynamic sorting in selectors
  });

/**
 * Extended state interface for syndicates list
 */
export interface SyndicateListEntityState extends EntityState<SyndicatesScope.Syndicate> {
  // ids: (number | string)[] - from EntityState
  // entities: { [id: string]: Syndicate } - from EntityState
  totalItems: number | undefined;
  haveItems: boolean;
  isLoading: boolean;
  pagination: SyndicatesScope.Pagination;
}

/**
 * Initial state with entity adapter
 */
export const initialSyndicateListState: SyndicateListEntityState = syndicateAdapter.getInitialState({
  totalItems: undefined,
  haveItems: true,
  isLoading: false,
  pagination: {
    page: 1,
    per: 25,
    sort_by: 'percentage_filled' as const,
    perform_total_count: false,
    filters: {
      status: 'available_to_fund',
      manager_limit: true
    }
  }
});

/**
 * Entity selectors provided by adapter
 */
export const {
  selectIds: selectSyndicateIds,
  selectEntities: selectSyndicateEntities,
  selectAll: selectAllSyndicates,
  selectTotal: selectTotalSyndicates
} = syndicateAdapter.getSelectors();

/**
 * NOTE: sortSyndicates function removed
 *
 * Sorting is handled entirely by the backend API.
 * The API endpoint accepts sort_by parameter and returns pre-sorted results.
 * This is necessary for "load more" pagination to maintain consistent order across pages.
 *
 * Frontend simply displays syndicates in the order received from API.
 */
